import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  BarChart3,
  ExternalLink,
  Gift,
  Lock,
  Monitor,
  RotateCcw,
  Scale,
  Sparkles,
  Trophy,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  COUNTRIES,
  EXPECTED_SCORE_SHEETS,
  GRAND_DRAW_NO,
  MOST_ACTIVE_PLACES,
} from '@sdg/shared'
import { AButton, Note, Section } from './Section'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../components/ui/dialog'
import { Field, Input } from '../../components/ui/input'
import { ScreenFocusBar } from './ScreenFocusBar'
import { cn } from '../../lib/cn'
import { api, get } from '../../lib/api'
import type {
  ActiveBoothsState,
  AttireEntry,
  AwardView,
  CeremonyState,
  DrawsState,
  DrawView,
  PendingPick,
  StageBusy,
  TieGroup,
} from './screenTypes'

/**
 * The Committee's controls for the big screen — slides 14 to 17 and 25 to 26.
 *
 * Every button here changes what the hall is looking at, so each one that
 * cannot be undone asks first, and the screen itself is one click away so
 * whoever is driving can see what the audience sees.
 */

export function CeremonySection() {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const draws = useQuery({
    queryKey: ['admin-draws'],
    queryFn: () => get<DrawsState>('/api/admin/draws', 'staff'),
    /*
     * A second while the screen is announcing, four otherwise.
     *
     * The buttons are disabled for the length of the animation, so the poll
     * has to be quick enough that they come back promptly afterwards —
     * waiting four seconds for a button to re-enable feels broken.
     */
    refetchInterval: (query) => (query.state.data?.stage?.busy ? 1000 : 4000),
    /*
     * Keep polling with the window behind the projector, and refresh the
     * moment it comes back. Both default to off, which is what left the
     * console showing a draw that had already moved on.
     */
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  })

  /** Counts down locally between polls, so the label does not sit still. */
  const stage = useStageBusy(draws.data?.stage)

  const ceremony = useQuery({
    queryKey: ['admin-ceremony'],
    queryFn: () => get<CeremonyState>('/api/admin/ceremony', 'staff'),
    refetchInterval: 4000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  })

  /**
   * Ties the judging panel still has to settle — slide 22.
   *
   * Read-only here. The decision belongs to the judges, who make it in their
   * own console; this exists so whoever is driving the ceremony can see *why*
   * "Publish winners" is refusing, and tell the panel which of them has not
   * voted yet instead of guessing.
   */
  const ties = useQuery({
    queryKey: ['admin-ties'],
    queryFn: () => get<{ ties: TieGroup[] }>('/api/admin/ties', 'staff'),
    refetchInterval: 4000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  })

  const act = useMutation({
    mutationFn: (path: string) => api(path, { method: 'POST', auth: 'staff' }),
    onSuccess: () => {
      setError(null)
      queryClient.invalidateQueries({ queryKey: ['admin-draws'] })
      queryClient.invalidateQueries({ queryKey: ['admin-ceremony'] })
      queryClient.invalidateQueries({ queryKey: ['admin-ties'] })
    },
    onError: (e) => setError(readableError(e)),
  })

  const run = (path: string) => {
    setError(null)
    act.mutate(path)
  }

  /**
   * Puts the presentation cheque on the stage screen.
   *
   * A focus change rather than a draw action: nothing about the draw itself
   * is altered, the projector is simply pointed somewhere else — the same
   * mechanism as the "back to the backdrop" control above, and the same way
   * back from it.
   */
  const showCheque = () => {
    setError(null)
    api('/api/admin/screen/focus', {
      method: 'POST',
      auth: 'staff',
      body: JSON.stringify({ mode: 'cheque' }),
    })
      .then(() => queryClient.invalidateQueries({ queryKey: ['admin-screen-focus'] }))
      .catch((e) => setError(readableError(e)))
  }


  return (
    <Section
      title="Big screen"
      description="Lucky draws and the award ceremony. Everything here appears on the stage screen immediately."
      actions={
        <AButton
          tone="outline"
          size="sm"
          onClick={() => window.open('/screen', 'sdg-screen', 'noopener')}
        >
          <Monitor className="size-3.5" strokeWidth={2.2} />
          Open the screen
          <ExternalLink className="size-3" strokeWidth={2.2} />
        </AButton>
      }
    >
      {error && (
        <Note tone="error" className="mb-3">
          {error}
        </Note>
      )}

      {/*
        What the screen is showing, and the one control that stops it.

        There was previously no way back to the welcome backdrop: once
        anything had been drawn the screen showed it for the rest of the day.
        This sits at the top because it is the button somebody reaches for in
        a hurry — between the draws and the ceremony, or when something is on
        screen that should not be.
      */}
      <ScreenFocusBar className="mb-4" onError={setError} />

      {/*
        The projector note is desktop-only. On a phone the section header
        already carries the description and the "Open the screen" button, so
        repeating it a third time pushed the actual controls below the fold —
        and nobody drives a projector from a phone anyway.
      */}
      <Note className="mb-4 hidden items-start gap-2.5 lg:flex">
        <Monitor className="mt-px size-4 shrink-0" strokeWidth={2.2} />
        <span>
          Open the screen in a second window and drag it to the projector. It needs no controls
          of its own — it follows whatever you do here.
        </span>
      </Note>

      {/* ── Lucky draws ── */}
      <div className="mb-2.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-[#0B1E3F]">
          <Gift className="size-4 text-orange-600" strokeWidth={2.2} />
          Lucky draws
        </h3>
        {draws.data && (
          <p className="text-xs text-slate-500">
            Eligible ({draws.data.threshold}+ booths):{' '}
            <b className="text-[#0B1E3F]">{draws.data.eligibleNow.toLocaleString()}</b>
            {draws.data.poolForOpenDraw != null && (
              <>
                {' · '}students in the open draw:{' '}
                <b className="text-[#0B1E3F]">{draws.data.poolForOpenDraw.toLocaleString()}</b>
              </>
            )}
          </p>
        )}
      </div>

      {draws.data && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {draws.data.draws.map((draw) => (
            <DrawCard
              key={draw.drawNo}
              draw={draw}
              busy={act.isPending}
              stage={stage}
              onRun={run}
              onShowCheque={showCheque}
            />
          ))}
        </div>
      )}

      {/* ── Award ceremony ── */}
      <h3 className="mb-2 mt-5 flex items-center gap-1.5 text-sm font-semibold text-[#0B1E3F]">
        <Trophy className="size-4 text-amber-700" strokeWidth={2.2} />
        Award ceremony
      </h3>

      {ceremony.data && (
        <>
          {!ceremony.data.judgingComplete && (
            <Note className="mb-3">
              <b>
                {ceremony.data.submittedSheets} of {ceremony.data.expectedSheets} score sheets in.
              </b>{' '}
              Winners can only be published once all three judges have submitted every sheet.
            </Note>
          )}

          {/*
            Shown between the sheet counter and the Publish button, which is
            the order things go wrong in: judging finishes, then a tie turns
            out to be blocking the podium.
          */}
          <TiePanel ties={ties.data?.ties ?? []} onRun={run} busy={act.isPending} />

          {/* Stacked and full-width on a phone: "Publish winners from the
              judges' scores" wrapped to three ragged lines inside a shrunken
              button when these sat side by side. */}
          <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <AButton
              tone="navy"
              size="sm"
              block
              className="sm:w-auto"
              disabled={!ceremony.data.judgingComplete || act.isPending}
              onClick={() => run('/api/admin/ceremony/publish')}
            >
              <Sparkles className="size-3.5 shrink-0" strokeWidth={2.2} />
              Publish winners
            </AButton>

            <AButton
              tone="outline"
              size="sm"
              block
              className="sm:w-auto"
              disabled={act.isPending}
              onClick={() => run('/api/admin/ceremony/reset')}
            >
              <RotateCcw className="size-3.5 shrink-0" strokeWidth={2.2} />
              Restart the ceremony
            </AButton>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {ceremony.data.awards.map((award) => (
              <AwardCard
                key={award.code}
                award={award}
                busy={act.isPending}
                stage={stage}
                onRun={run}
              />
            ))}
          </div>
        </>
      )}
    </Section>
  )
}

function DrawCard({
  draw,
  busy,
  stage,
  onRun,
  onShowCheque,
}: {
  draw: DrawView
  busy: boolean
  /** The big screen is mid-announcement; every stage button waits. */
  stage: { busy: boolean; seconds: number }
  onRun: (path: string) => void
  /** Grand Draw only — points the projector at the presentation cheque. */
  onShowCheque: () => void
}) {
  const [confirmDraw, setConfirmDraw] = useState(false)

  /*
   * The held name, fetched per card. Only an open draw can have one, so the
   * other two cards never ask — and it refetches on a short interval so a
   * second console showing the same draw does not sit on a stale pick.
   */
  const pickQuery = useQuery({
    queryKey: ["admin-pick", draw.drawNo],
    queryFn: () =>
      get<{ pick: PendingPick | null }>(`/api/admin/draws/${draw.drawNo}/pick`, "staff"),
    enabled: draw.status === "open",
    refetchInterval: 4000,
  })

  const pick = pickQuery.data?.pick ?? null

  /** Picking and discarding are card-local: neither touches the screen. */
  const pickAction = useMutation({
    mutationFn: (method: "POST" | "DELETE") =>
      api(`/api/admin/draws/${draw.drawNo}/pick`, { method, auth: "staff" }),
    onSettled: () => pickQuery.refetch(),
  })

  const picking = pickAction.isPending || pickQuery.isFetching
  const [redrawPos, setRedrawPos] = useState<number | null>(null)
  const [confirmReset, setConfirmReset] = useState(false)
  /** Typed confirmation: restarting discards real winners. */
  const [resetTyped, setResetTyped] = useState('')

  const themes = {
    navy: 'ring-blue-300',
    purple: 'ring-violet-300',
    gold: 'ring-amber-300',
  } as const

  /*
   * "Complete" and "closed early" are both `status: 'complete'` on the server
   * — closing a draw ends it whether or not every winner was drawn. Showing
   * one label for both read as "COMPLETE · 0 of 5 drawn", which is a
   * contradiction: it looks like a bug in the draw rather than a draw the
   * Committee chose to stop.
   */
  const finished = draw.status === 'complete'
  const allDrawn = draw.winners.length >= draw.winnersTarget

  const badge = !finished
    ? draw.status === 'open'
      ? { text: 'Open', tone: 'bg-emerald-100 text-emerald-600' }
      : { text: 'Pending', tone: 'bg-amber-100 text-amber-700' }
    : allDrawn
      ? { text: 'Complete', tone: 'bg-slate-100 text-slate-500' }
      : { text: 'Closed early', tone: 'bg-amber-100 text-amber-700' }

  return (
    // `ring-inset`: an outward ring paints outside the content box and is the
    // first thing a scroll edge clips. Inset, it cannot be cut.
    <div
      className={cn(
        'rounded-2xl border border-white bg-white/95 p-4 shadow-lg shadow-blue-900/5 ring-2 ring-inset',
        themes[draw.theme],
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-tight text-[#0B1E3F]">{draw.name}</p>
          <p className="text-xs text-slate-500">
            {finished && draw.winners.length === 0
              ? 'Closed before any winner was drawn'
              : `${draw.winners.length} of ${draw.winnersTarget} drawn`}
          </p>
        </div>
        <span
          className={cn(
            'shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide',
            badge.tone,
          )}
        >
          {badge.text}
        </span>
      </div>

      {/* A draw stopped short needs saying once, plainly, with the way back. */}
      {finished && !allDrawn && (
        <p className="mt-2 text-xs text-amber-700">
          {draw.winnersTarget - draw.winners.length} of {draw.winnersTarget} prizes were not
          drawn. Restart the draw to run it again from a fresh pool.
        </p>
      )}

      {/*
        Slide 14's re-draw, in two steps.

        It used to be one: "Absent" went straight to the replacement, which
        deleted the row and inserted a new one in the same transaction — so the
        screen only ever saw the result. The name changed with no drum-roll and
        nothing to explain it. Marking absent is now its own act: it puts
        "not present" on the screen while the calling happens, and the
        replacement is a second, separate decision. Reversible until then.
      */}
      {draw.winners.length > 0 && (
        <ul className="mt-2.5 divide-y divide-slate-100 text-xs">
          {draw.winners.map((w) => (
            <li
              key={w.position}
              className={cn(
                'flex items-center gap-2 py-1.5',
                w.absent && '-mx-1 rounded-lg bg-red-50/70 px-1',
              )}
            >
              <span
                className={cn(
                  'grid size-5 shrink-0 place-items-center rounded-full text-xs font-semibold tabular-nums',
                  w.absent ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500',
                )}
              >
                {w.position}
              </span>
              <span className="min-w-0 flex-1">
                <span
                  className={cn(
                    'block truncate font-semibold',
                    w.absent ? 'text-slate-400 line-through' : 'text-[#0B1E3F]',
                  )}
                >
                  {w.name}
                </span>
                <span
                  className={cn(
                    'block truncate text-xs',
                    w.absent ? 'font-semibold text-red-600' : 'text-slate-500',
                  )}
                >
                  {w.absent ? 'Not present · on screen now' : w.school}
                  {!w.absent && w.replacedPosition && ' · re-drawn'}
                </span>
              </span>
              {/*
                Available on a *completed* draw too, not only an open one. A
                draw marks itself complete the moment its last winner is
                drawn — and the Grand Draw has one winner, so it completes
                instantly. Gating these on `status === 'open'` meant the one
                winner most likely to be called three times was the one with
                no absent control at all.
              */}
              {/*
                Proper buttons, not text links.

                These are never blocked by the announcement guard — the hall
                calls a name three times, which takes longer than any
                animation — but next to a big "Screen is announcing · 9s"
                button that *is* greyed out, a small grey link read as part of
                the same disabled group. Outlined and coloured, they read as
                available.

                They wait for the announcement like everything else here.
                Marking a winner absent swaps the wheel for the absent card,
                and a replacement starts a second drum-roll over the first —
                both change what the hall is looking at mid-sentence.
              */}
              {draw.openedAt != null &&
                (w.absent ? (
                  <span className="flex shrink-0 items-center gap-1.5">
                    <button
                      type="button"
                      disabled={busy || stage.busy}
                      onClick={() => onRun(`/api/admin/draws/${draw.drawNo}/present/${w.position}`)}
                      className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-600 hover:border-slate-400 hover:text-slate-800 disabled:opacity-50"
                    >
                      Came forward
                    </button>
                    <button
                      type="button"
                      disabled={busy || stage.busy}
                      onClick={() => setRedrawPos(w.position)}
                      className="rounded-lg border border-red-300 bg-red-50 px-2 py-1 text-xs font-semibold text-red-700 hover:border-red-400 hover:bg-red-100 disabled:opacity-50"
                    >
                      Draw replacement…
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    disabled={busy || stage.busy}
                    onClick={() => onRun(`/api/admin/draws/${draw.drawNo}/absent/${w.position}`)}
                    className="shrink-0 rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-600 hover:border-red-300 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                  >
                    Mark absent
                  </button>
                ))}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex flex-col gap-1.5">
        {draw.status === 'pending' && (
          <AButton
            size="sm"
            block
            disabled={busy || stage.busy}
            onClick={() => onRun(`/api/admin/draws/${draw.drawNo}/open`)}
          >
            <Lock className="size-3.5" strokeWidth={2.2} />
            {stage.busy
              ? `Screen is announcing · ${stage.seconds}s`
              : 'Open this lucky draw'}
          </AButton>
        )}

        {draw.status === 'open' && (
          <>
            {/*
              Two steps, because the Committee wants to see the name before
              the hall does. "Pick" chooses privately and shows it here only —
              nothing reaches the projector. From there it is either announced,
              which spins the wheel, or set aside for another name.

              The pick is not blocked by the announcement guard: nothing is
              shown, so there is nothing to talk over. Only announcing waits.
            */}
            {pick ? (
              <PendingPickCard
                pick={pick}
                busy={busy || picking}
                stage={stage}
                onAnnounce={() => setConfirmDraw(true)}
                onAnother={() => pickAction.mutate("POST")}
                onDiscard={() => pickAction.mutate("DELETE")}
              />
            ) : (
              <AButton
                tone="navy"
                block
                size="sm"
                disabled={busy || picking}
                onClick={() => pickAction.mutate("POST")}
              >
                <Sparkles className="size-3.5" strokeWidth={2.2} />
                {picking ? "Picking…" : `Pick winner ${draw.winners.length + 1}`}
              </AButton>
            )}
            {/*
              Closing is refused while prizes are still to be drawn — one
              stray click used to end Lucky Draw 1 with four of its five
              prizes undrawn, and the only way back was restarting from a
              fresh pool. The server enforces it; this says why before the
              click rather than after.
            */}
            <AButton
              tone="outline"
              size="sm"
              block
              disabled={busy || draw.closeBlock != null}
              onClick={() => onRun(`/api/admin/draws/${draw.drawNo}/close`)}
            >
              Close this lucky draw
            </AButton>
            {draw.closeBlock === 'incomplete' && (
              <p className="px-0.5 text-xs leading-snug text-slate-500">
                {draw.winnersTarget - draw.winners.length} of {draw.winnersTarget} prizes are
                still to be drawn. The draw closes itself once the last winner is drawn.
              </p>
            )}
            {draw.closeBlock === 'unresolved_absence' && (
              <p className="px-0.5 text-xs leading-snug text-amber-700">
                A winner is marked not present. Use “Came forward” or draw a replacement before
                closing.
              </p>
            )}
            {/*
              The one case where closing is allowed with prizes left: nobody
              eligible remains, so the draw can never finish. It is also the
              one case where the click loses something, so it says so.
            */}
            {draw.closeBlock == null && draw.winners.length < draw.winnersTarget && (
              <p className="px-0.5 text-xs leading-snug text-amber-700">
                No eligible students are left. Closing will end the draw with{' '}
                {draw.winnersTarget - draw.winners.length} of {draw.winnersTarget} prizes undrawn.
              </p>
            )}
          </>
        )}

        {/*
          The presentation cheque, for the Grand Draw only.

          Offered once the draw is complete and a winner has actually come
          forward: a cheque with a name on it that has been called three times
          and not answered is the one thing this must not put on a projector.
          Putting it up is a deliberate act, because the hall should read the
          winner's name on the draw screen first — so this is a button rather
          than something that follows the draw automatically.
        */}
        {draw.drawNo === GRAND_DRAW_NO &&
          draw.status === 'complete' &&
          draw.winners.some((w) => !w.absent) && (
            <AButton
              tone="navy"
              block
              disabled={busy}
              onClick={() => onShowCheque()}
            >
              <Trophy className="size-4" strokeWidth={2.2} />
              Show the cheque
            </AButton>
          )}

        {/*
          Available while open as well as after finishing: a draw started by
          a mis-click, or opened before the queue had finished scanning,
          needs a way back — and on event day, not only in rehearsal.
        */}
        {draw.status !== 'pending' && (
          <AButton
            tone="outline"
            size="sm"
            block
            disabled={busy}
            onClick={() => setConfirmReset(true)}
          >
            <RotateCcw className="size-3.5" strokeWidth={2.2} />
            Restart this draw
          </AButton>
        )}
      </div>

      {/* Drawing is irreversible in front of an audience, so it asks. */}
      <Dialog open={confirmDraw} onOpenChange={setConfirmDraw}>
        <DialogContent className="rounded-3xl p-0" showClose={false}>
          <div className="px-6 pb-6 pt-7 text-center">
            <span className="mx-auto mb-3 grid size-12 place-items-center rounded-full bg-amber-100 text-amber-700">
              <Sparkles className="size-5" strokeWidth={2.2} />
            </span>
            <DialogTitle className="text-base">
              {pick ? `Announce ${pick.name}?` : `Draw winner ${draw.winners.length + 1}?`}
            </DialogTitle>
            <DialogDescription className="mt-1.5">
              {pick
                ? "The hall has not seen this name yet. Announcing spins the wheel and reveals it."
                : "A name will be picked and announced in one step."}{" "}
              This cannot be undone — an absent winner is handled with a re-draw.
            </DialogDescription>

            <div className="mt-5 flex gap-2.5">
              <AButton tone="outline" block disabled={busy} onClick={() => setConfirmDraw(false)} autoFocus>
                Not yet
              </AButton>
              <AButton
                tone="navy"
                block
                disabled={busy}
                onClick={() => {
                  setConfirmDraw(false)
                  onRun(`/api/admin/draws/${draw.drawNo}/draw`)
                  /* The pick is consumed server-side; drop the stale copy. */
                  void pickQuery.refetch()
                }}
              >
                {pick ? "Announce now" : "Draw now"}
              </AButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/*
        Restarting throws away winners who may already have been announced,
        so it asks for the draw's name to be typed rather than a single tap.
      */}
      <Dialog
        open={confirmReset}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmReset(false)
            setResetTyped('')
          }
        }}
      >
        <DialogContent className="rounded-3xl p-0" showClose={false}>
          <div className="p-6">
            <DialogTitle className="text-base">Restart {draw.name}?</DialogTitle>
            <DialogDescription className="mt-1.5">
              {draw.winners.length > 0 ? (
                <>
                  This removes {draw.winners.length}{' '}
                  {draw.winners.length === 1 ? 'winner' : 'winners'} and puts{' '}
                  {draw.winners.length === 1 ? 'them' : 'them'} back in the pool. Use it if the
                  draw was opened or run by mistake — not to change a result someone has already
                  been told about.
                </>
              ) : (
                <>This closes the draw back to pending so a fresh pool is taken when you reopen it.</>
              )}
            </DialogDescription>

            {draw.winners.length > 0 && (
              <div className="mt-4">
                <label
                  htmlFor={`reset-${draw.drawNo}`}
                  className="text-sm font-medium text-[#0B1E3F]"
                >
                  Type <span className="font-semibold">{draw.name}</span> to confirm
                </label>
                <Input
                  id={`reset-${draw.drawNo}`}
                  className="mt-1.5"
                  value={resetTyped}
                  onChange={(e) => setResetTyped(e.target.value)}
                  autoComplete="off"
                  autoFocus
                />
              </div>
            )}

            <div className="mt-6 flex justify-end gap-2">
              <AButton
                tone="outline"
                disabled={busy}
                onClick={() => {
                  setConfirmReset(false)
                  setResetTyped('')
                }}
              >
                Cancel
              </AButton>
              <AButton
                tone="navy"
                disabled={
                  busy || (draw.winners.length > 0 && resetTyped.trim() !== draw.name)
                }
                onClick={() => {
                  setConfirmReset(false)
                  setResetTyped('')
                  onRun(`/api/admin/draws/${draw.drawNo}/reset`)
                }}
              >
                Restart draw
              </AButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Slide 14: "winner not present after 3 calls → re-draw". */}
      <Dialog open={redrawPos != null} onOpenChange={(open) => !open && setRedrawPos(null)}>
        <DialogContent className="rounded-3xl p-0" showClose={false}>
          <div className="px-6 pb-6 pt-7 text-center">
            <DialogTitle className="text-base">
              Draw a replacement for winner {redrawPos}?
            </DialogTitle>
            <DialogDescription className="mt-1.5">
              Only after three calls, as the rules require. The screen will show the drum-roll and
              land a new name in position {redrawPos}. The absent student leaves the position,
              becomes eligible again, and both are recorded in the log. Use “Came forward” instead
              if they have turned up.
            </DialogDescription>

            <div className="mt-5 flex gap-2.5">
              <AButton tone="outline" block disabled={busy} onClick={() => setRedrawPos(null)} autoFocus>
                Cancel
              </AButton>
              <AButton
                tone="navy"
                block
                disabled={busy}
                onClick={() => {
                  const pos = redrawPos
                  setRedrawPos(null)
                  onRun(`/api/admin/draws/${draw.drawNo}/redraw/${pos}`)
                }}
              >
                Yes, draw a replacement
              </AButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function AwardCard({
  award,
  busy,
  stage,
  onRun,
}: {
  award: AwardView
  busy: boolean
  /** The big screen is mid-announcement; revealing waits for it. */
  stage: { busy: boolean; seconds: number }
  onRun: (path: string) => void
}) {
  const [editing, setEditing] = useState(false)

  const nextPlace = award.revealOrder.find(
    (p) => !award.results.find((r) => r.place === p)?.revealed,
  )

  /*
   * Admin's own view of the attire winners.
   *
   * `award.results` withholds a name until it is revealed, because the same
   * payload drives the public screen. Admin has to be able to check that the
   * code they entered resolved to the right student *before* it goes up, so
   * that comes from the Admin-only route instead.
   */
  const attire = useQuery({
    queryKey: ['admin-attire'],
    queryFn: () => get<{ entries: AttireEntry[] }>('/api/admin/ceremony/attire', 'staff'),
    enabled: award.code === 'traditional_attire',
  })

  const entered = (category: 'male' | 'female') =>
    attire.data?.entries.find((e) => e.category === category)

  return (
    <div className="rounded-2xl border border-white bg-white/95 p-4 shadow-lg shadow-blue-900/5">
      <p className="text-sm font-semibold leading-tight text-[#0B1E3F]">{award.title}</p>
      <p className="text-xs text-slate-500">
        {award.source === 'judges'
          ? "From judges' scores"
          : award.source === 'scans'
            ? 'From booth attendance'
            : 'Entered by Admin'}{' '}
        · {award.revealedCount}/{award.places} revealed
      </p>

      <ul className="mt-2 space-y-1 text-xs">
        {award.results.map((r) => {
          const row =
            award.code === 'traditional_attire'
              ? entered(r.label === 'Male' ? 'male' : 'female')
              : undefined

          return (
            <li key={r.place} className="flex items-center gap-2">
              <span
                className={cn(
                  'grid size-5 shrink-0 place-items-center rounded-full text-xs font-semibold',
                  r.revealed ? 'bg-amber-400 text-[#0B1E3F]' : 'bg-slate-100 text-slate-400',
                )}
              >
                {award.code === 'traditional_attire' ? r.label[0] : r.place}
              </span>
              <span className="min-w-0 flex-1">
                {award.code === 'traditional_attire' ? (
                  row ? (
                    <>
                      <span className="block truncate font-semibold text-[#0B1E3F]">
                        <span className="font-mono tabular-nums text-slate-500">{row.regNo}</span>{' '}
                        {row.name}
                      </span>
                      <span className="block truncate text-slate-500">
                        {[row.attire, row.country].filter(Boolean).join(' · ') || 'no attire entered'}
                        {!r.revealed && ' · not revealed'}
                      </span>
                    </>
                  ) : (
                    <span className="text-slate-400">no winner entered</span>
                  )
                ) : r.revealed ? (
                  <>
                    <span className="block truncate font-semibold text-[#0B1E3F]">{r.label}</span>
                    {/* Most Active School: the booth number under the school. */}
                    {award.source === 'scans' && r.detail && (
                      <span className="block truncate text-slate-500">{r.detail}</span>
                    )}
                  </>
                ) : (
                  <span className="text-slate-400">not revealed</span>
                )}
              </span>

              {/* The counted figure, where a judged award shows its score. */}
              {r.metric && (
                <span className="shrink-0 font-semibold tabular-nums text-orange-600">
                  {r.metric.value.toLocaleString()}
                </span>
              )}
              {/*
                `displayScore`, so this matches the projector exactly. Where
                the judging panel had to separate two places that scored the
                same, the lower one reads a hundredth down — two places
                holding an identical number looks like a fault. The judges'
                real figure is in Results and in the CSV export.
              */}
              {r.displayScore != null && (
                <span
                  className="shrink-0 font-semibold tabular-nums text-orange-600"
                  title={
                    r.finalScore != null && r.finalScore !== r.displayScore
                      ? `Judges' score ${r.finalScore.toFixed(2)} — tied, separated by the judging panel`
                      : undefined
                  }
                >
                  {r.displayScore.toFixed(2)}
                </span>
              )}
            </li>
          )
        })}
      </ul>

      {/*
        Most Active Clubs has no button. Its six clubs are a fixed roster in
        `CLUBS`, decided by the Committee in advance — a form for them only
        offered a way to misspell a club onto the projector minutes before it
        is read out. Revealing them one by one is the only action left, and
        that is the same "Reveal" control every other award uses.
      */}
      {award.source === 'admin' && award.code !== 'most_active_club' && (
        <AButton tone="outline" size="sm" block className="mt-2.5" onClick={() => setEditing(true)}>
          Enter winners
        </AButton>
      )}

      {/*
        Most Active School has its own publish, separate from "Publish
        winners" at the top of the section: that one is gated on every score
        sheet being in, and this award has nothing to do with the judges.
      */}
      {award.source === 'scans' && (
        <AButton tone="outline" size="sm" block className="mt-2.5" onClick={() => setEditing(true)}>
          <BarChart3 className="size-3.5" strokeWidth={2.2} />
          {award.published ? 'Review standings' : 'Publish standings'}
        </AButton>
      )}

      <AButton
        tone={award.complete ? 'outline' : 'navy'}
        size="sm"
        block
        className="mt-1.5"
        disabled={busy || award.complete || !award.published || stage.busy}
        onClick={() => onRun(`/api/admin/ceremony/${award.code}/reveal`)}
      >
        <Trophy className="size-3.5" strokeWidth={2.2} />
        {award.complete
          ? 'All revealed'
          : !award.published
            ? 'Not published yet'
            : stage.busy
              ? `Screen is announcing · ${stage.seconds}s`
              : award.places === 1
                ? 'Reveal the winner'
                : award.code === 'traditional_attire'
                  ? 'Reveal next'
                  : `Reveal ${ordinal(nextPlace ?? 1)}`}
      </AButton>

      {editing && award.code === 'traditional_attire' && (
        <AttireDialog onClose={() => setEditing(false)} />
      )}
      {editing && award.source === 'scans' && (
        <ActiveBoothsDialog onClose={() => setEditing(false)} />
      )}
    </div>
  )
}

/**
 * Most Active School — the two booths that drew the most students.
 *
 * The only award decided by the data rather than by a person, so this dialog
 * is mostly a window onto the count: the live ranking, and a Publish that
 * freezes first and second.
 *
 * The one judgement it does ask for is a tie. Two booths level on 480 visits
 * in 1st and 2nd is not the sort order's decision, so publishing refuses
 * until the Committee says who takes which place — and it can only reorder
 * booths that are *level*. The server re-checks that, because the award's
 * whole claim is that it was counted.
 */
function ActiveBoothsDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const [order, setOrder] = useState<number[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const state = useQuery({
    queryKey: ['admin-active-booths'],
    queryFn: () => get<ActiveBoothsState>('/api/admin/ceremony/active-booths', 'staff'),
  })

  const scanned = (state.data?.standings ?? []).filter((s) => s.scans > 0)
  /*
   * First and second — `MOST_ACTIVE_PLACES`, the same constant the server
   * publishes against. It was hardcoded to 1 here, which is why this dialog
   * went on showing a single winner after the award became two: the preview
   * said one booth, and a tie made publishing fail outright because the order
   * it sent was shorter than the podium the server was building.
   */
  const podiumSize = Math.min(MOST_ACTIVE_PLACES, scanned.length)

  /**
   * Places the hall has already heard. Fixed, and the server enforces it.
   *
   * This award is re-published *after* first place is announced, to pick up
   * second — and students keep scanning in between, so by then the live
   * ranking has moved. Without pinning, the dialog would offer a school for a
   * place that was read out on stage twenty minutes earlier.
   */
  const announced = new Map(
    (state.data?.published ?? [])
      .filter((p) => p.revealed && p.place <= podiumSize)
      .map((p) => [p.place, p] as const),
  )
  const spoken = new Set([...announced.values()].map((p) => p.boothId))

  /** The order the counts alone produce — the starting point, and the default. */
  const natural = (() => {
    const queue = scanned.filter((s) => !spoken.has(s.boothId))
    return Array.from({ length: podiumSize }, (_, slot) => announced.get(slot + 1) ?? queue.shift())
  })()

  const contested = state.data?.contested ?? []
  const chosen = order ?? natural.flatMap((s) => (s ? [s.boothId] : []))

  const boothById = (id: number) => scanned.find((s) => s.boothId === id)

  /**
   * Who may stand in this slot: every booth on the same count as the slot's
   * natural occupant. Equals only — that is the whole rule, and it is why a
   * swap between two offered booths can never contradict the ranking.
   *
   * An announced place offers nobody: it is already decided.
   */
  const candidatesFor = (slot: number) =>
    announced.has(slot + 1)
      ? []
      : scanned.filter((s) => !spoken.has(s.boothId) && s.scans === natural[slot]?.scans)

  const place = (slot: number, boothId: number) => {
    setError(null)
    setOrder(() => {
      const next = [...chosen]
      const heldAt = next.indexOf(boothId)
      // Already on the podium: swap the two, so nobody is listed twice.
      if (heldAt >= 0) next[heldAt] = next[slot]
      next[slot] = boothId
      return next
    })
  }

  const publish = useMutation({
    mutationFn: () =>
      api('/api/admin/ceremony/active-booths/publish', {
        method: 'POST',
        auth: 'staff',
        body: JSON.stringify(contested.length > 0 ? { order: chosen } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-ceremony'] })
      queryClient.invalidateQueries({ queryKey: ['admin-active-booths'] })
      onClose()
    },
    onError: (e) => setError(readableError(e)),
  })

  const alreadyPublished = (state.data?.published.length ?? 0) > 0
  const anyRevealed = state.data?.published.some((p) => p.revealed) ?? false

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-125 p-0">
        <div className="px-6 pb-6 pt-7">
          <DialogTitle className="text-[17px]">Most Active School</DialogTitle>
          <DialogDescription className="mt-1.5">
            Ranked by the number of students marked at each booth. Publishing freezes first
            and second place — the count keeps moving, the published figures do not.
          </DialogDescription>

          {state.isPending && <p className="mt-4 text-sm text-slate-500">Counting…</p>}

          {state.data && scanned.length === 0 && (
            <Note tone="attention" className="mt-4">
              No booth has been scanned yet, so there is nothing to rank.
            </Note>
          )}

          {state.data && scanned.length > 0 && (
            <>
              {contested.length > 0 && (
                <Note tone="attention" className="mt-4">
                  <b className="font-bold">A place is tied.</b> Two or more booths finished on
                  the same count, so the order is yours to decide. You can only choose between
                  booths that are level — the count itself is the attendance figure and is not
                  editable.
                </Note>
              )}

              {/* The podium as it will be published. */}
              <ul className="mt-4 space-y-2">
                {Array.from({ length: podiumSize }, (_, slot) => {
                  const held = announced.get(slot + 1)
                  const booth = held ?? boothById(chosen[slot])
                  const options = candidatesFor(slot)
                  const choosable = options.length > 1

                  return (
                    <li key={slot} className="rounded-xl border border-slate-200 bg-white p-3">
                      <div className="flex items-center gap-2.5">
                        <span
                          className={cn(
                            'grid size-7 shrink-0 place-items-center rounded-full text-xs font-bold',
                            /* Gold for the winner, quieter for the runner-up. */
                            slot === 0
                              ? 'bg-amber-400 text-[#0B1E3F]'
                              : 'bg-slate-200 text-slate-600',
                          )}
                        >
                          {slot + 1}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-bold text-[#0B1E3F]">
                            {booth?.school ?? '—'}
                          </span>
                          <span className="block text-xs text-slate-500">
                            Booth {booth?.boothNo ?? '—'}
                            {held && ' · announced, fixed'}
                          </span>
                        </span>
                        <span className="shrink-0 text-sm font-bold tabular-nums text-orange-600">
                          {booth?.scans.toLocaleString() ?? 0}
                        </span>
                      </div>

                      {choosable && (
                        <div className="mt-2.5 border-t border-slate-100 pt-2.5">
                          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400">
                            Tied on {options[0].scans.toLocaleString()} — who takes{' '}
                            {slot === 0 ? 'first' : 'second'}?
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {options.map((option) => (
                              <button
                                key={option.boothId}
                                type="button"
                                aria-pressed={option.boothId === chosen[slot]}
                                onClick={() => place(slot, option.boothId)}
                                className={cn(
                                  'rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors',
                                  option.boothId === chosen[slot]
                                    ? 'border-orange-400 bg-orange-50 text-orange-700'
                                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                                )}
                              >
                                Booth {option.boothNo}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>

              {/* The full ranking, so the podium can be checked against it. */}
              <details className="mt-3">
                <summary className="cursor-pointer text-xs font-semibold text-slate-500 hover:text-slate-700">
                  Full ranking ({scanned.length} booths scanned)
                </summary>
                <ul className="mt-2 space-y-1 text-xs">
                  {scanned.map((s) => (
                    <li key={s.boothId} className="flex items-center gap-2">
                      <span className="w-5 shrink-0 text-right tabular-nums text-slate-400">
                        {s.place}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-slate-600">
                        Booth {s.boothNo} · {s.school}
                      </span>
                      {s.tied && (
                        <span className="shrink-0 rounded-full bg-amber-100 px-1.5 text-[10px] font-bold text-amber-700">
                          tied
                        </span>
                      )}
                      <span className="shrink-0 font-semibold tabular-nums text-slate-700">
                        {s.scans.toLocaleString()}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>

              {anyRevealed && (
                <Note tone="attention" className="mt-3">
                  Some places have already been announced. Re-publishing refreshes the figures but
                  never un-reveals what the hall has seen.
                </Note>
              )}

              {error && <Note tone="error" className="mt-3">{error}</Note>}

              <div className="mt-5 flex gap-2.5">
                <AButton tone="outline" block onClick={onClose} disabled={publish.isPending}>
                  Close
                </AButton>
                <AButton
                  tone="navy"
                  block
                  disabled={publish.isPending}
                  onClick={() => publish.mutate()}
                >
                  <Trophy className="size-4" strokeWidth={2.2} />
                  {publish.isPending
                    ? 'Publishing…'
                    : alreadyPublished
                      ? 'Re-publish'
                      : podiumSize > 1
                        ? 'Publish winners'
                        : 'Publish winner'}
                </AButton>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * The name the system has picked, shown to Admin and nobody else.
 *
 * Deliberately styled as a held, unresolved thing rather than as a result:
 * amber rather than the green of a drawn winner, and it says in words that
 * the hall cannot see it. Whoever is driving the ceremony is looking at a
 * name the audience is not, and the console should never let that be
 * ambiguous.
 *
 * "Announce" is the only button with weight. Picking again is deliberately
 * quieter — it is a correction, not the normal path, and every use of it is
 * written to the audit log.
 */
function PendingPickCard({
  pick,
  busy,
  stage,
  onAnnounce,
  onAnother,
  onDiscard,
}: {
  pick: PendingPick
  busy: boolean
  stage: { busy: boolean; seconds: number }
  onAnnounce: () => void
  onAnother: () => void
  onDiscard: () => void
}) {
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50/70 p-2.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-bold uppercase tracking-wide text-amber-800">
          Picked for prize {pick.position}
        </span>
        <span className="text-[11px] font-semibold text-amber-700">Not on screen</span>
      </div>

      <p className="mt-1 truncate text-sm font-bold text-[#0B1E3F]">{pick.name}</p>
      <p className="truncate text-xs text-slate-500">
        {pick.school}
        {pick.regNo && ` · ${pick.regNo}`}
      </p>

      <div className="mt-2.5 flex flex-col gap-1.5">
        <AButton
          tone="navy"
          block
          size="sm"
          disabled={busy || stage.busy}
          onClick={onAnnounce}
        >
          <Sparkles className="size-3.5" strokeWidth={2.2} />
          {stage.busy ? `Screen is announcing · ${stage.seconds}s` : 'Announce — spin the wheel'}
        </AButton>

        <div className="flex gap-1.5">
          <button
            type="button"
            disabled={busy}
            onClick={onAnother}
            className="flex-1 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs font-semibold text-slate-600 hover:border-slate-400 hover:text-slate-800 disabled:opacity-50"
          >
            Pick someone else
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onDiscard}
            className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs font-semibold text-slate-500 hover:border-slate-400 hover:text-slate-700 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

interface AttireDraft {
  regNo: string
  attire: string
  countryCode: string
}

const EMPTY_DRAFT: AttireDraft = { regNo: '', attire: '', countryCode: '' }

/**
 * Best Traditional Attire is not judged, so the Committee enters it — by
 * registration code, not by typing a name.
 *
 * The code is looked up as it is typed and the student it resolves to is
 * shown, because the thing this prevents is a misspelt or wrong name reaching
 * a 3-metre screen with no way to tell it was wrong. Name and school come
 * from the register; only the attire and its country are typed, and the
 * country is chosen from the event's own list rather than free text.
 */
function AttireDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const [drafts, setDrafts] = useState<Record<'male' | 'female', AttireDraft>>({
    male: EMPTY_DRAFT,
    female: EMPTY_DRAFT,
  })
  const [error, setError] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)

  const existing = useQuery({
    queryKey: ['admin-attire'],
    queryFn: () => get<{ entries: AttireEntry[] }>('/api/admin/ceremony/attire', 'staff'),
  })

  // Pre-fill once, so a correction starts from what is already entered
  // instead of from blank fields that would overwrite it.
  useEffect(() => {
    if (loaded || !existing.data) return
    setLoaded(true)
    setDrafts({
      male: draftFrom(existing.data.entries.find((e) => e.category === 'male')),
      female: draftFrom(existing.data.entries.find((e) => e.category === 'female')),
    })
  }, [existing.data, loaded])

  const save = useMutation({
    mutationFn: async () => {
      for (const category of ['male', 'female'] as const) {
        const draft = drafts[category]
        if (!draft.regNo.trim()) continue

        await api(`/api/admin/ceremony/attire/${category}`, {
          method: 'POST',
          body: JSON.stringify({
            regNo: draft.regNo.trim().toUpperCase(),
            attire: draft.attire.trim() || null,
            countryCode: draft.countryCode || null,
          }),
          auth: 'staff',
        })
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-ceremony'] })
      queryClient.invalidateQueries({ queryKey: ['admin-attire'] })
      onClose()
    },
    onError: (e) => setError(readableError(e)),
  })

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[88dvh] overflow-y-auto rounded-3xl p-0">
        <div className="px-5 pb-5 pt-6">
          <DialogTitle className="text-base">Best Traditional Attire</DialogTitle>
          <DialogDescription className="mt-1">
            Two winners, entered by their registration code. The name and school come from the
            register. Nothing appears on the screen until it is revealed.
          </DialogDescription>

          {(['male', 'female'] as const).map((category) => {
            const draft = drafts[category]
            const set = (next: Partial<AttireDraft>) =>
              setDrafts((all) => ({ ...all, [category]: { ...all[category], ...next } }))

            return (
              <div key={category} className="mt-4 rounded-2xl border border-slate-200 p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-orange-600">
                  {category}
                </p>
                <div className="space-y-2">
                  <Field label="Registration code" htmlFor={`${category}-reg`}>
                    <Input
                      id={`${category}-reg`}
                      value={draft.regNo}
                      placeholder="A001"
                      autoComplete="off"
                      spellCheck={false}
                      className="font-mono uppercase tabular-nums"
                      onChange={(e) => set({ regNo: e.target.value.toUpperCase() })}
                    />
                  </Field>

                  <ResolvedStudent regNo={draft.regNo} />

                  <div className="grid gap-2 sm:grid-cols-2">
                    <Field label="Attire" htmlFor={`${category}-attire`}>
                      <Input
                        id={`${category}-attire`}
                        value={draft.attire}
                        placeholder="Baju Melayu"
                        onChange={(e) => set({ attire: e.target.value })}
                      />
                    </Field>
                    <Field label="Country" htmlFor={`${category}-country`}>
                      <select
                        id={`${category}-country`}
                        value={draft.countryCode}
                        onChange={(e) => set({ countryCode: e.target.value })}
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-[#0B1E3F] outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                      >
                        <option value="">Not stated</option>
                        {COUNTRIES.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>
                </div>
              </div>
            )
          })}

          {error && (
            <Note tone="error" className="mt-3">
              {error}
            </Note>
          )}

          <div className="mt-4 flex gap-2.5">
            <AButton tone="outline" block onClick={onClose} disabled={save.isPending}>
              Cancel
            </AButton>
            <AButton tone="navy" block onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save winners'}
            </AButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function draftFrom(entry: AttireEntry | undefined): AttireDraft {
  return entry
    ? {
        regNo: entry.regNo ?? '',
        attire: entry.attire ?? '',
        countryCode: entry.countryCode ?? '',
      }
    : EMPTY_DRAFT
}

/**
 * Who the code points at, looked up while it is being typed.
 *
 * The register is searched rather than trusted blindly: an exact match on the
 * code is required, so A01 part-way through typing A012 shows nothing instead
 * of showing the wrong student.
 */
function ResolvedStudent({ regNo }: { regNo: string }) {
  const code = regNo.trim().toUpperCase()
  const valid = /^[A-Z]\d{1,3}$/.test(code)

  const found = useQuery({
    queryKey: ['admin-reg-lookup', code],
    queryFn: () =>
      get<{ students: { regNo: string | null; name: string; school: string }[] }>(
        `/api/admin/students?q=${encodeURIComponent(code)}&limit=5`,
        'staff',
      ),
    enabled: valid,
  })

  if (!code) return null

  if (!valid) {
    return <p className="px-0.5 text-xs text-slate-400">Codes look like A001.</p>
  }

  if (found.isLoading) {
    return <p className="px-0.5 text-xs text-slate-400">Looking up {code}…</p>
  }

  const student = found.data?.students.find((s) => s.regNo === code)

  if (!student) {
    return (
      <p className="px-0.5 text-xs font-semibold text-red-600">
        No student is registered as {code}.
      </p>
    )
  }

  return (
    <p className="rounded-xl bg-emerald-50 px-2.5 py-1.5 text-xs text-emerald-800">
      <b className="font-semibold">{student.name}</b> · {student.school}
    </p>
  )
}

function ordinal(n: number): string {
  return n === 1 ? '1st place' : n === 2 ? '2nd place' : `${n}rd place`
}

/**
 * The ties blocking the podium, and how the judging panel has voted —
 * slide 22's third tie-break.
 *
 * Read-only on purpose. Admin pressing "Publish winners" is the moment this
 * becomes a problem, so it belongs here, but the decision itself is the
 * judges' and is made in their console. All Admin can do is see who is
 * outstanding — and discard the ballots if the panel wants to start over.
 */
function TiePanel({
  ties,
  onRun,
  busy,
}: {
  ties: TieGroup[]
  onRun: (path: string) => void
  busy: boolean
}) {
  const [confirming, setConfirming] = useState<string | null>(null)

  if (ties.length === 0) return null

  return (
    <>
      {ties.map((tie) => {
        const waiting = tie.votes.filter((v) => v.ordering == null)
        const settled = tie.resolved != null
        const winner = settled
          ? tie.entries.find((e) => e.subjectKey === tie.resolved![0])?.label
          : null

        return (
          <Note
            key={`${tie.rubricCode}:${tie.place}`}
            tone={settled ? undefined : 'error'}
            className="mb-3 block"
          >
            <p className="flex flex-wrap items-center gap-x-1.5 font-semibold text-[#0B1E3F]">
              <Scale className="size-4 shrink-0 text-amber-700" strokeWidth={2.2} />
              {tie.awardTitle} — {ordinal(tie.place)} is tied:{' '}
              {tie.entries.map((e) => e.label).join(' and ')}
              <span className="font-mono text-xs tabular-nums text-slate-500">
                {tie.entries[0]?.finalScore.toFixed(2)} each
              </span>
            </p>

            <p className="mt-1.5 text-xs leading-relaxed">
              Level on the final score and on the three highest-weighted criteria, so the
              scores cannot separate them. The judging panel decides, in the judges' own
              console.
            </p>

            <ul className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-xs">
              {tie.votes.map((vote) => (
                <li key={vote.judgeId} className="flex items-center gap-1.5">
                  <span className="font-semibold text-[#0B1E3F]">{vote.judgeLabel}</span>
                  {vote.ordering ? (
                    <span className="truncate font-semibold text-emerald-700">
                      {tie.entries.find((e) => e.subjectKey === vote.ordering![0])?.label}
                    </span>
                  ) : (
                    <span className="text-slate-400">not voted</span>
                  )}
                </li>
              ))}
            </ul>

            <p className="mt-2.5 text-xs font-semibold">
              {settled ? (
                <span className="text-emerald-700">
                  Panel decision: {winner} takes {ordinal(tie.place).toLowerCase()}. Winners can
                  be published.
                </span>
              ) : waiting.length > 0 ? (
                <span>
                  Waiting on {waiting.map((v) => v.judgeLabel).join(' and ')} before winners can
                  be published.
                </span>
              ) : (
                <span>
                  The panel is split evenly. They need to confer and change a decision — the
                  podium cannot be published while two entries share a place.
                </span>
              )}
            </p>

            <AButton
              tone="outline"
              size="sm"
              className="mt-2.5"
              disabled={busy || tie.votes.every((v) => v.ordering == null)}
              onClick={() => setConfirming(tie.rubricCode)}
            >
              <RotateCcw className="size-3.5 shrink-0" strokeWidth={2.2} />
              Discard the panel's decisions
            </AButton>
          </Note>
        )
      })}

      <Dialog open={confirming != null} onOpenChange={(open) => !open && setConfirming(null)}>
        <DialogContent>
          <div className="px-6 pb-6 pt-7">
            <DialogTitle className="text-lg font-bold text-[#0B1E3F]">
              Discard the panel's decisions?
            </DialogTitle>
            <DialogDescription className="mt-1.5 text-sm text-slate-500">
              Every ballot the judges have cast for this award is deleted, and they will be
              asked to decide again. Use this only if the panel wants to start over.
            </DialogDescription>

            <div className="mt-4 flex gap-2.5">
              <AButton tone="outline" block onClick={() => setConfirming(null)}>
                Keep them
              </AButton>
              <AButton
                tone="navy"
                block
                disabled={busy}
                onClick={() => {
                  onRun(`/api/admin/ties/${confirming}/clear`)
                  setConfirming(null)
                }}
              >
                Yes, discard
              </AButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

/** Turns the server's refusal codes into something readable on stage. */
/**
 * The screen's busy state, ticking down between polls.
 *
 * The server is the authority — it refuses the action either way — but a
 * button that says "Screen is announcing · 4s" is a different experience from
 * one that looks available and then fails. The local tick just keeps the
 * number honest in the gap between one poll and the next.
 */
function useStageBusy(stage: StageBusy | undefined): { busy: boolean; seconds: number } {
  const [now, setNow] = useState(() => Date.now())
  const anchor = useRef({ at: 0, ms: 0 })

  useEffect(() => {
    if (!stage?.busy) return
    anchor.current = { at: Date.now(), ms: stage.msRemaining }
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), 250)
    return () => window.clearInterval(timer)
  }, [stage?.busy, stage?.msRemaining])

  if (!stage?.busy) return { busy: false, seconds: 0 }

  const left = anchor.current.ms - (now - anchor.current.at)
  if (left <= 0) return { busy: false, seconds: 0 }

  return { busy: true, seconds: Math.ceil(left / 1000) }
}

function readableError(e: unknown): string {
  const message = e instanceof Error ? e.message : String(e)

  const map: Record<string, string> = {
    another_draw_open: 'Another draw is still open. Close it before opening this one.',
    already_open: 'That draw is already open.',
    already_complete: 'That draw has already finished.',
    full: 'Every winner for that draw has been drawn.',
    pool_empty: 'Nobody is left in the pool for that draw.',
    not_open: 'Open the draw before drawing a winner.',
    judging_incomplete: `All ${EXPECTED_SCORE_SHEETS} score sheets must be submitted before winners are published.`,
    unresolved_tie:
      'Two entries are still tied on the scores. The judging panel decides it in their own console — see the tie above for who has not voted yet.',
    nothing_left: 'Every place for that award has been revealed.',
    not_published: 'Publish the winners before revealing them.',
    not_allowed_in_production: 'Resetting is disabled once the event is live.',
    /*
     * One announcement at a time. The buttons are disabled while this is
     * true, but the refusal still reaches here if a second Admin on another
     * laptop presses theirs at the same moment.
     */
    stage_busy: 'The screen is still announcing. Wait for it to finish.',
    incomplete: 'Prizes are still to be drawn. The draw closes itself after the last one.',
    unresolved_absence: 'A winner is marked not present. Resolve that before closing.',
    not_absent: 'Mark the winner absent before drawing a replacement.',

    /*
     * Most Active School and Most Active Club. These were missing, so every
     * one of them fell through to "That did not work" — including the tie,
     * which is not a failure at all but a decision waiting for the
     * Committee, and the one message that most needed to say what to do.
     */
    tie: 'Two or more booths finished level at the top. Choose which one takes the award, then publish.',
    no_scans: 'No booth has been scanned yet, so there is nothing to rank.',
    invalid_order:
      'That order does not match the attendance counts. You can only choose between booths that are level.',
    name_required: 'Name at least one club before saving.',
  }

  return map[message] ?? 'That did not work. Check the screen and try again.'
}
